import { useEffect, useRef, useState } from "react";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

/**
 * Returns a signed offset (-1 → 1) based on the element's center
 * relative to the viewport center. 0 when the element is centered.
 * Useful for counter-parallax (move two things in opposite directions).
 * Driven by the shared rAF-batched scroll engine, transform-only friendly.
 */
export function useCounterParallax<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) return; // stay static

    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const wh = window.innerHeight;
      const elCenter = rect.top + rect.height / 2;
      const viewCenter = wh / 2;
      // normalize: range roughly -1 (below) .. 1 (above)
      const o = (viewCenter - elCenter) / (wh / 2 + rect.height / 2);
      setOffset(Math.max(-1, Math.min(1, o)));
    });
  }, []);

  return { ref, offset };
}
